import React, { useState, useEffect, useRef } from 'react'

import AutocompleteCityOrigin from '../../Autocomplete/AutocompleteCityOrigin'

function OriginFilters(props) {
	const {
		filters,
		handleFilterChange,
		handleOriginChange,
		labels,
		styles,
		isDarkMode,
		onPickupLocationsChange, // New prop for real-time updates
	} = props

	const [locations, setLocations] = useState([])
	const containerRef = useRef(null)
	// Two effects both touch this data: the [filters.pickupLocations] sync below
	// projects filters → locations; the [locations] push effect below projects
	// locations → filters. With an empty initial `locations` and a populated
	// `filters.pickupLocations` (e.g. restored from a search tab), the push effect
	// would wipe filters and kick off an infinite oscillation. Only push from the
	// [locations] effect when the change was driven by an explicit user action
	// (autocomplete add/remove). Set this flag right before any setLocations
	// call that represents a user action.
	const userInitiatedChange = useRef(false)

	// Load locations from localStorage on component mount
	useEffect(() => {
		try {
			const stored = localStorage.getItem('originLocations')
			if (stored) {
				const storedLocations = JSON.parse(stored)
				const recentLocations = storedLocations
					.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
					.slice(0, 10)

				const locationsWithSelection = recentLocations.map(loc => ({
					...loc,
					isSelected: false,
				}))
				setLocations(locationsWithSelection)

				if (recentLocations.length !== storedLocations.length) {
					localStorage.setItem(
						'originLocations',
						JSON.stringify(recentLocations)
					)
				}
			}
		} catch (error) {
			console.error('Error loading origin locations:', error)
			setLocations([])
		}
	}, [])

	// Sync local state when filter state changes.
	// Match by name (stable across browsers); merge in any filter locations not yet in
	// local state so saved searches applied on a different browser still populate the field.
	useEffect(() => {
		setLocations(prev => {
			const filterNames = new Set(
				filters.pickupLocations.map(loc => loc.name)
			)

			const updated = prev.map(loc => ({
				...loc,
				isSelected: filterNames.has(loc.name),
			}))

			const existingNames = new Set(updated.map(l => l.name))
			const missing = filters.pickupLocations
				.filter(loc => loc.name && !existingNames.has(loc.name))
				.map(loc => ({
					id: loc.id || `${loc.name}|${loc.lat}|${loc.lng}`,
					name: loc.name,
					lat: loc.lat,
					lng: loc.lng,
					timestamp: new Date().toISOString(),
					isSelected: true,
				}))

			if (missing.length === 0) {
				const hasChanges = updated.some(
					(loc, index) => loc.isSelected !== prev[index].isSelected
				)
				return hasChanges ? updated : prev
			}

			return [...missing, ...updated].slice(0, 10)
		})
	}, [filters.pickupLocations])

	// Save locations to localStorage and notify parent when locations or radius change
	useEffect(() => {
		if (locations.length > 0) {
			// Remove isSelected before saving to localStorage
			const locationsToSave = locations.map(({ isSelected, ...rest }) => rest)
			localStorage.setItem('originLocations', JSON.stringify(locationsToSave))
		}

		// Only push back to the parent when the user actively toggled a selection;
		// otherwise we'd undo filters that came from a restored tab or saved search.
		if (!userInitiatedChange.current) return
		userInitiatedChange.current = false

		// Get selected locations for filter state
		const selectedLocs = locations.filter(loc => loc.isSelected)

		// Convert to format with display info and notify parent component
		const pickupLocations = selectedLocs.map(city => ({
			id: city.id,
			name: city.name,
			lat: city.lat,
			lng: city.lng,
		}))

		// Send real-time updates to parent
		if (onPickupLocationsChange) {
			onPickupLocationsChange(pickupLocations)
		}
	}, [locations])

	// Helper function to add location and toggle its selection in one operation
	const addLocationAndToggle = cityData => {
		const locationName = `${cityData.city}, ${cityData.state_id}`

		userInitiatedChange.current = true
		setLocations(prev => {
			const existingIndex = prev.findIndex(loc => loc.name === locationName)

			if (existingIndex >= 0) {
				// Toggle existing location (selection only in state)
				const updated = [...prev]
				updated[existingIndex] = {
					...updated[existingIndex],
					isSelected: !updated[existingIndex].isSelected,
					timestamp: new Date().toISOString(),
				}
				return updated
			} else {
				// Add new location as selected (selection only in state)
				const newLocation = {
					id: locationName,
					name: locationName,
					lat: cityData.lat || 0,
					lng: cityData.lng || 0,
					isSelected: true, // This is only in state, not saved to localStorage
					timestamp: new Date().toISOString(),
				}
				const newList = [newLocation, ...prev]
				return newList.length > 10 ? newList.slice(0, 10) : newList
			}
		})
	}

	const handleLabelClick = e => {
		// Use the parent element to scope the query to only this component's input
		const container = e.target.closest('.input-container')
		if (container) {
			const inputElement = container.querySelector('input')
			if (inputElement) {
				inputElement.focus()
			}
		}
	}

	// Extend styles to ensure consistent height with select
	const inputStyles = {
		...styles,
		height: '48px', // Increased height to match the select element
		boxSizing: 'border-box',
	}

	// Common focus styles for both input and select
	const focusRingClass =
		'focus:ring focus:ring-opacity-50 focus:border-blue-300 focus:ring-blue-200'

	// Input class with focus ring styling
	const inputClassName = `input input-bordered join-item shrink mt-1 p-2 rounded-md w-full ${focusRingClass}`

	// Select class with matching focus ring styling - removed daisyUI classes
	const selectClassName = `border join-item mt-1 py-2 rounded-lg w-full h-14 ${focusRingClass} ${
		isDarkMode
			? 'bg-gray-800 border-gray-600 text-white'
			: 'bg-white border-gray-300 text-gray-900'
	}`

	return (
		<div className="join flex flex-1">
			<div className="input-container flex-grow">
				<div ref={containerRef}>
					<AutocompleteCityOrigin
						origin={filters.origin}
						style={inputStyles}
						className={`${inputClassName} z-60`}
						name="origin"
						handleOriginChange={handleOriginChange}
						isDarkMode={isDarkMode}
						locations={locations}
						onToggleSelection={addLocationAndToggle}
					/>

					<label
						className={
							filters.pickupCity ||
							locations.filter(loc => loc.isSelected).length > 0
								? 'filled'
								: ''
						}
						style={{
							...labels,
							backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
							color: isDarkMode ? '#C9D1D9' : '#3a4353',
						}}
						onClick={handleLabelClick}
					>
						Origin{' '}
						<span style={{ fontSize: '0.99em', opacity: 0.7 }}>(up to 3)</span>
					</label>
				</div>
			</div>

			{/* Origin Radius */}
			<div className="radius-select select-wrap">
				<label
					style={{
						...labels,
						backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
						color: isDarkMode ? '#C9D1D9' : '#3a4353',
					}}
				>
					Radius
				</label>
				<select
					name="pickupMaxDist"
					onChange={handleFilterChange}
					className={selectClassName}
					style={styles}
					value={filters.pickupMaxDist}
					disabled={
						!filters.pickupCity && !locations.some(loc => loc.isSelected)
					}
				>
					<option value={10}>10 mi</option>
					<option value={25}>25 mi</option>
					<option value={50}>50 mi</option>
					<option value={75}>75 mi</option>
					<option value={100}>100 mi</option>
					<option value={125}>125 mi</option>
					<option value={150}>150 mi</option>
					<option value={200}>200 mi</option>
					<option value={250}>250 mi</option>
				</select>
			</div>
		</div>
	)
}

export default OriginFilters
